import { Locale } from "../Locale.ts";

/**
 * The doubleNumberLine manipulative's labels (ADR 0129). The `sentence*` templates are what the marker
 * reads: the slider's aria-valuetext and the live readout. The names a line starts with (`ratioTop`
 * and so on) are only defaults; the adult can type their own, and those are kept in component state.
 */
const en = {
    "doubleNumberLine.lineLabel": "Double number line: {top} over {bottom}",
    "doubleNumberLine.sliderLabel": "Marker on both number lines",
    "doubleNumberLine.sentencePair": "{topLabel}: {top}, {bottomLabel}: {bottom}",
    "doubleNumberLine.sentencePercent": "{percent}% of {whole} is {amount}",
    "doubleNumberLine.sentenceIncrease": "{percent}% of {whole} is {amount}, an increase of {change}%, or {changeAmount}",
    "doubleNumberLine.sentenceDecrease": "{percent}% of {whole} is {amount}, a decrease of {change}%, or {changeAmount}",
    "doubleNumberLine.sentenceSame": "{percent}% of {whole} is {amount}, no change",
    "doubleNumberLine.ratioTop": "Cups of juice",
    "doubleNumberLine.ratioBottom": "Cups of water",
    "doubleNumberLine.unitRateTop": "Dollars",
    "doubleNumberLine.unitRateBottom": "Notebooks",
    "doubleNumberLine.amount": "Amount",
    "doubleNumberLine.percent": "Percent",
    "doubleNumberLine.unitRateHint": "Cut each step into 4 parts to find the price of one notebook.",
    "doubleNumberLine.perStep": "{label} in each step",
    "doubleNumberLine.whole": "Whole amount",
    "doubleNumberLine.steps": "Steps",
    "doubleNumberLine.parts": "Parts in each step",
    "doubleNumberLine.decrease": "Decrease {name}",
    "doubleNumberLine.increase": "Increase {name}",
    "doubleNumberLine.limitReason": "Steps times parts can be at most 60.",
    "doubleNumberLine.showTable": "Show as a table",
    "doubleNumberLine.tableLabel": "Pairs on the double number line",
    "doubleNumberLine.topName": "Name of the top line",
    "doubleNumberLine.bottomName": "Name of the bottom line",
} as const;

export type DoubleNumberLineStringKey = keyof typeof en;

const es = {
    "doubleNumberLine.lineLabel": "Recta numérica doble: {top} sobre {bottom}",
    "doubleNumberLine.sliderLabel": "Marcador en las dos rectas numéricas",
    "doubleNumberLine.sentencePair": "{topLabel}: {top}, {bottomLabel}: {bottom}",
    "doubleNumberLine.sentencePercent": "{percent}% de {whole} es {amount}",
    "doubleNumberLine.sentenceIncrease": "{percent}% de {whole} es {amount}, un aumento de {change}%, o {changeAmount}",
    "doubleNumberLine.sentenceDecrease": "{percent}% de {whole} es {amount}, una disminución de {change}%, o {changeAmount}",
    "doubleNumberLine.sentenceSame": "{percent}% de {whole} es {amount}, sin cambio",
    "doubleNumberLine.ratioTop": "Tazas de jugo",
    "doubleNumberLine.ratioBottom": "Tazas de agua",
    "doubleNumberLine.unitRateTop": "Dólares",
    "doubleNumberLine.unitRateBottom": "Cuadernos",
    "doubleNumberLine.amount": "Cantidad",
    "doubleNumberLine.percent": "Porcentaje",
    "doubleNumberLine.unitRateHint": "Divide cada paso en 4 partes para hallar el precio de un cuaderno.",
    "doubleNumberLine.perStep": "{label} en cada paso",
    "doubleNumberLine.whole": "Total",
    "doubleNumberLine.steps": "Pasos",
    "doubleNumberLine.parts": "Partes en cada paso",
    "doubleNumberLine.decrease": "Disminuir {name}",
    "doubleNumberLine.increase": "Aumentar {name}",
    "doubleNumberLine.limitReason": "Los pasos por las partes pueden ser 60 como máximo.",
    "doubleNumberLine.showTable": "Mostrar como tabla",
    "doubleNumberLine.tableLabel": "Pares de la recta numérica doble",
    "doubleNumberLine.topName": "Nombre de la recta de arriba",
    "doubleNumberLine.bottomName": "Nombre de la recta de abajo",
} satisfies Record<DoubleNumberLineStringKey, string>;

const fr = {
    "doubleNumberLine.lineLabel": "Double droite numérique : {top} au-dessus de {bottom}",
    "doubleNumberLine.sliderLabel": "Repère sur les deux droites numériques",
    "doubleNumberLine.sentencePair": "{topLabel} : {top}, {bottomLabel} : {bottom}",
    "doubleNumberLine.sentencePercent": "{percent} % de {whole}, c'est {amount}",
    "doubleNumberLine.sentenceIncrease": "{percent} % de {whole}, c'est {amount}, une augmentation de {change} %, soit {changeAmount}",
    "doubleNumberLine.sentenceDecrease": "{percent} % de {whole}, c'est {amount}, une diminution de {change} %, soit {changeAmount}",
    "doubleNumberLine.sentenceSame": "{percent} % de {whole}, c'est {amount}, aucun changement",
    "doubleNumberLine.ratioTop": "Tasses de jus",
    "doubleNumberLine.ratioBottom": "Tasses d'eau",
    "doubleNumberLine.unitRateTop": "Dollars",
    "doubleNumberLine.unitRateBottom": "Cahiers",
    "doubleNumberLine.amount": "Quantité",
    "doubleNumberLine.percent": "Pourcentage",
    "doubleNumberLine.unitRateHint": "Coupez chaque pas en 4 parts pour trouver le prix d'un cahier.",
    "doubleNumberLine.perStep": "{label} dans chaque pas",
    "doubleNumberLine.whole": "Total",
    "doubleNumberLine.steps": "Pas",
    "doubleNumberLine.parts": "Parts dans chaque pas",
    "doubleNumberLine.decrease": "Diminuer {name}",
    "doubleNumberLine.increase": "Augmenter {name}",
    "doubleNumberLine.limitReason": "Les pas multipliés par les parts font 60 au plus.",
    "doubleNumberLine.showTable": "Afficher sous forme de tableau",
    "doubleNumberLine.tableLabel": "Paires de la double droite numérique",
    "doubleNumberLine.topName": "Nom de la droite du haut",
    "doubleNumberLine.bottomName": "Nom de la droite du bas",
} satisfies Record<DoubleNumberLineStringKey, string>;

export const doubleNumberLineStrings: Record<Locale, Record<DoubleNumberLineStringKey, string>> = { en, es, fr };
